/**
 * Reporting Hierarchy API Contract
 *
 * Manages manager-employee reporting relationships within a company.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { ReportingHierarchyResponseSchema, } from "@hourly/api/employment";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Reporting Hierarchy Contracts
// ============================================
/**
 * GET /reporting-hierarchy/{employmentId}
 * Get reporting hierarchy for an employment
 */
export const getReportingHierarchy = oc
    .route({
    method: "GET",
    path: "/reporting-hierarchy/{employmentId}",
    summary: "Get reporting hierarchy for an employment",
    tags: ["Reporting Hierarchy"],
})
    .input(z.object({ employmentId: uuidSchema }))
    .output(ReportingHierarchyResponseSchema);
/**
 * POST /reporting-hierarchy/{employmentId}/managers/{managerId}
 * Add a manager to an employment
 */
export const addManagerToHierarchy = oc
    .route({
    method: "POST",
    path: "/reporting-hierarchy/{employmentId}/managers/{managerId}",
    summary: "Add a manager to an employment",
    tags: ["Reporting Hierarchy"],
    successStatus: 201,
})
    .input(z.object({ employmentId: uuidSchema, managerId: uuidSchema }))
    .output(ReportingHierarchyResponseSchema);
/**
 * DELETE /reporting-hierarchy/{employmentId}/managers/{managerId}
 * Remove a manager from an employment
 */
export const removeManagerFromHierarchy = oc
    .route({
    method: "DELETE",
    path: "/reporting-hierarchy/{employmentId}/managers/{managerId}",
    summary: "Remove a manager from an employment",
    tags: ["Reporting Hierarchy"],
})
    .input(z.object({ employmentId: uuidSchema, managerId: uuidSchema }))
    .output(ReportingHierarchyResponseSchema);
/**
 * PUT /reporting-hierarchy/{employmentId}/managers
 * Replace all managers for an employment
 */
export const setManagersForHierarchy = oc
    .route({
    method: "PUT",
    path: "/reporting-hierarchy/{employmentId}/managers",
    summary: "Replace all managers for an employment",
    tags: ["Reporting Hierarchy"],
})
    .input(z.object({
    employmentId: uuidSchema,
    managerIds: z.array(uuidSchema).max(50),
}))
    .output(ReportingHierarchyResponseSchema);
/**
 * PUT /reporting-hierarchy/{employmentId}/direct-reports
 * Replace all direct reports for an employment
 */
export const setDirectReportsForHierarchy = oc
    .route({
    method: "PUT",
    path: "/reporting-hierarchy/{employmentId}/direct-reports",
    summary: "Replace all direct reports for an employment",
    tags: ["Reporting Hierarchy"],
})
    .input(z.object({
    employmentId: uuidSchema,
    directReportIds: z.array(uuidSchema).max(100),
}))
    .output(ReportingHierarchyResponseSchema);
// ============================================
// Contract Router
// ============================================
export const reportingHierarchyContract = {
    get: getReportingHierarchy,
    addManager: addManagerToHierarchy,
    removeManager: removeManagerFromHierarchy,
    setManagers: setManagersForHierarchy,
    setDirectReports: setDirectReportsForHierarchy,
};
//# sourceMappingURL=reportingHierarchy.js.map